<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col sm="12">
        <b-form @submit.prevent="submit">
          <b-card>
            <div slot="header">
              <strong>{{ $t('messages.securityInformation' )}}</strong>
            </div>

            <b-form-group :label="`${$t('messages.socialSecurityNumber')}*`" description="ex. 999-99-9999">
              <b-input-group>
                <div class="input-group-prepend">
                  <span class="input-group-text">
                    <i class='fa fa-male'></i>
                  </span>
                </div>
                <masked-input
                  type="tel"
                  @keypress="isNumber($event, 'applicant.ssn')"
                  name="applicant.ssn"
                  v-validate="{
                    required: true,
                    regex: /^[0-9]{3}\-?[0-9]{2}\-?[0-9]{4}$/
                  }"
                  :class="{
                    'form-control': true,
                    'is-valid': validateState('applicant.ssn') === true,
                    'is-invalid': validateState('applicant.ssn') === false,
                  }"
                  v-model="applicant.ssn"
                  :mask="[/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]"
                  :guide="true"
                  placeholderChar="#">
                </masked-input>
              </b-input-group>
            </b-form-group>
            <b-form-group :label="`${$t('messages.dateOfBirth')}*`" description="ex. 99/99/9999">
              <b-input-group>
                <div class="input-group-prepend">
                  <span class="input-group-text">
                    <i class='fa fa-calendar'></i>
                  </span>
                </div>
                <masked-input
                  type="text"
                  name="applicant.dob"
                  class="form-control"
                  @keypress="isNumber($event, 'applicant.dob')"
                  v-model="applicant.dob"
                  v-validate="{
                    required: true,
                    regex: /^\d{2}\/\d{2}\/\d{4}$/
                  }"
                  :class="{
                    'form-control': true,
                    'is-valid': validateState('applicant.dob') === true,
                    'is-invalid': validateState('applicant.dob') === false,
                  }"
                  :mask="[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]"
                  :guide="true"
                  placeholderChar="_"
                  :showMask="true"
                  :keepCharPositions="true"
                  :pipe="autoCorrectedDatePipe()">
                </masked-input>
              </b-input-group>
            </b-form-group>
          </b-card>
          <b-card>
            <div slot="header">
              <strong>{{ $t('messages.employmentInformation') }}</strong>
            </div>
            <b-form-group v-if="!!applicant">
              <label :for="`applicant.employment_status`">{{ $t('messages.employmentStatus') }}</label>
              <b-form-select
                :name="`applicant.employment_status`"
                :data-vv-as="$t('messages.employmentStatus')"
                v-model="applicant.employment_status"
                v-validate="'required'"
                :state="validateState(`applicant.employment_status`)"
                :placeholder="$t('messages.employmentStatus')"
                :options="employmentOptions">
              </b-form-select>
            </b-form-group>
            <b-row
              v-show="applicant.employment_status"
              :class="{ 'tecobi-credit-app-employment': true, 'valid-employment': (validateState(`applicant.employment_${index}_years`) && validateState(`applicant.employment_${index}_months`)) }"
              v-for="(x, index) in [].constructor(employmentsNeeded)"
              :key="index">
              <b-col
                v-if="showEmploymentFields"
                sm="12">
                <b-form-group>
                  <label :for="`applicant.employment_${index}_employer_name`">{{ index > 0 ? $t('messages.previous') : $t('messages.current') }}  {{ applicant.employment_status === 'Student' ? $t('messages.college') : $t('messages.employer') }}* <em v-show="index > 0">(We need {{ totalYearsNeeded }} years history)</em></label>
                  <b-form-input
                    type="text"
                    v-validate="'required'"
                    :state="validateState(`applicant.employment_${index}_employer_name`)"
                    :name="`applicant.employment_${index}_employer_name`"
                    data-vv-as="Employer Name"
                    v-model="applicant[`employment_${index}_employer_name`]"
                    :placeholder="$t('messages.enterName')"></b-form-input>
                </b-form-group>
                <b-form-group v-if="['Employed', 'Active Military', 'Other', 'Self-Employed'].includes(applicant.employment_status)">
                  <label :for="`applicant.employment_${index}_work_title`">{{ $t('messages.workTitle') }}</label>
                  <b-form-input
                    type="text"
                    :state="validateState(`applicant.employment_${index}_work_title`)"
                    :name="`applicant.employment_${index}_work_title`"
                    :data-vv-as="$t('messages.workTitle')"
                    v-model="applicant[`employment_${index}_work_title`]"
                    :placeholder="$t('messages.workTitle')"></b-form-input>
                </b-form-group>
                <b-row>
                  <b-col sm="6">
                    <b-form-group>
                      <label :for="`applicant.employment_${index}_contact_name`">{{ $t('messages.contactName') }}</label>
                      <b-form-input
                        type="text"
                        v-validate="'required'"
                        :state="validateState(`applicant.employment_${index}_contact_name`)"
                        :name="`applicant.employment_${index}_contact_name`"
                        data-vv-as="City"
                        v-model="applicant[`employment_${index}_contact_name`]"
                        :placeholder="$t('messages.enterContactName')"></b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col sm="6">
                    <b-form-group description="ex. (999) 999-9999">
                        <label :for="`applicant.employment_${index}_contact_phone`">{{ $t('messages.contactPhone') }} *</label>
                        <masked-input
                          type="input"
                          :name="`applicant.employment_${index}_contact_phone`"
                          @keyup="phoneTouches[`employment${index}PhoneTouched`] = true"
                          :class="'form-control ' + phoneValidationCssClass(index)"
                          v-model="applicant[`employment_${index}_contact_phone`]"
                          :mask="['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]"
                          :guide="true"
                          :placeholder="$t('messages.enterContactPhone')"
                          placeholderChar="#">
                        </masked-input>
                        <div class="invalid-feedback">{{ $t('messages.mustBeValidPhone') }}</div>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-col>
              <b-col
                v-if="['Employed', 'Active Military'].includes(applicant.employment_status)"
                sm="6">
                <b-form-group>
                  <label :for="`applicant.employment_${index}_years`">{{ $t('messages.timeAtEmployer') }}</label>
                  <b-form-select
                    :name="`applicant.employment_${index}_years`"
                    data-vv-as="Employment Years"
                    :options="yearsOptions"
                    v-model="applicant[`employment_${index}_years`]"
                    v-validate="'required'"
                    :state="validateState(`applicant.employment_${index}_years`)"
                    type="text"></b-form-select>
                </b-form-group>
              </b-col>
              <b-col
                v-if="['Employed', 'Active Military'].includes(applicant.employment_status)"
                sm="6">
                <b-form-group>
                <label :for="`applicant.employment_${index}_months`">&nbsp;</label>
                  <b-form-select
                    :name="`applicant.employment_${index}_months`"
                    data-vv-as="Employment months"
                    :options="monthsOptions"
                    v-model="applicant[`employment_${index}_months`]"
                    v-validate="'required'"
                    :state="validateState(`applicant.employment_${index}_months`)"
                    type="text"></b-form-select>
                </b-form-group>
              </b-col>
              <b-col
                v-if="index === 0 && ['Employed', 'Active Military', 'Retired', 'Retired Military', 'Self-Employed'].includes(applicant.employment_status)"
                sm="6">
                <b-form-group>
                  <label :for="`applicant.employment_${index}_salary_type`">{{ $t('messages.salaryWagesIncome') }}*</label>
                  <b-form-select
                    :name="`applicant.employment_${index}_salary_type`"
                    data-vv-as="Salary Type"
                    :options="salaryTypeOptions"
                    v-model="applicant[`employment_${index}_salary_type`]"
                    v-validate="'required'"
                    :state="validateState(`applicant.employment_${index}_salary_type`)"
                    type="text"></b-form-select>
                </b-form-group>
              </b-col>
              <b-col
                v-if="index === 0 && ['Employed', 'Active Military', 'Retired', 'Retired Military', 'Self-Employed'].includes(applicant.employment_status)"
                sm="6">
                <b-form-group>
                <label :for="`applicant.employment_${index}_salary_amount`">&nbsp;</label>
                  <b-input-group prepend="$">
                    <b-form-input
                      :name="`applicant.employment_${index}_salary_amount`"
                      data-vv-as="Salary amount"
                      @keypress="isNumber($event)"
                      :placeholder="$t('messages.salaryAmount')"
                      v-model="applicant[`employment_${index}_salary_amount`]"
                      v-validate="'required|numeric'"
                      :state="validateState(`applicant.employment_${index}_salary_amount`)"
                      type="tel"></b-form-input>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card>
          <b-card>
            <div slot="header">
              <i class="fa fa-edit"></i> {{ $t("messages.agreeToTerms") }}
            </div>
            <b-form-group>
              <b-form-checkbox
                name="applicant.terms_agreed"
                v-validate="'required'"
                v-model="applicant.terms_agreed">
                <p class="disclosure">{{ $t('messages.termsBlurbFullApp') }}</p>
                <p>
                  <b-button type="button" variant="link" @click="privacyModal = true">{{ $t('messages.privacyPolicy') }}</b-button>
                  <b-button type="button" variant="link" @click="termsModal = true">{{ $t('messages.termsOfUse') }}</b-button>
                </p>
              </b-form-checkbox>
            </b-form-group>
            <div slot="footer">
              <b-button
                :disabled="submitting || veeErrors.any() || !applicant.terms_agreed"
                type="submit"
                size="md"
                variant="primary">
                <i class="fa fa-dot-circle-o"></i>
                {{ $t('messages.submit') }}</b-button>
            </div>
          </b-card>
        </b-form>
      </b-col>
    </b-row>
    <!-- Modal Component -->
    <b-modal v-model="termsModal" :title="$t('messages.termsOfUse')" @ok="termsModal = false" size="lg">
      <p>The words "you," "your" and "yours" mean each person submitting this application. The words "we," "us," "our" and "ours" as used below refer to the dealer and to the Financial Institutions selected to receive your application.</p><p>You authorize the dealer, as part of the credit underwriting process, to submit this application and any other application submitted in connection with the proposed transaction to the Financial Institutions disclosed to you by the dealer, for review. In addition, in accordance with the Fair Credit Reporting Act, you authorize that such Financial Institutions may submit your applications for review to other Financial Institutions that may want to purchase your contract.</p><p>You agree that we and any Financial Institutions to which your application is submitted may obtain a consumer credit report periodically from one or more consumer reporting agencies (credit bureaus) in connection with the proposed transaction and any update, renewal, refinancing, modification or extension of that transaction.</p><p>You agree that we may verify your employment, pay, assets and debts, and that anyone receiving a copy of this is authorized to provide us with such information.</p><p>You further authorize us to gather whatever credit and employment history we consider necessary and appropriate in evaluating this application and any other applications submitted in connection with the proposed transaction.</p><p>We may keep this application and any other application submitted to us, and information about you whether or not the application is approved. You certify that the information on this application and in any other application submitted to us, is true and complete. You understand that false statements may subject you to criminal penalties.</p><p>FEDERAL NOTICES</p><p>IMPORTANT INFORMATION ABOUT PROCEDURES FOR OPENING A NEW ACCOUNT</p><p>To help the government fight the funding of terrorism and money laundering activities, Federal law requires all financial institutions to obtain, verify, and record information that identifies each person who opens an account. What this means for you: When you open an account, we will ask for your name,address, date of birth, and other information that will allow us to identify you. We may also ask to see your driver's license or other identifying documents.</p><p>STATE NOTICES</p><p>Arizona, California, Idaho, Louisiana, Nevada, New Mexico, Texas, Washington or Wisconsin Residents: If you, the applicant, are married and live in a community property state, you may be contacted by the dealer to provide the personal credit information on your spouse in the co-applicant section. Your spouse is not required to be a co-applicant for the credit requested unless he/she wishes to be a co-applicant.</p><p>California Residents: An applicant, if married, may apply for a separate account.</p><p>Maine Residents: You may purchase required insurance through any insurance agent or broker and from any insurance company that is reasonably acceptable to us. You are not required to deal with any of our affiliates when choosing an agent, broker or insurer. Your choice of a particular insurance agent, broker or insurer will not affect our credit decision, so long as the insurance provides adequate coverage with an insurer who meets our reasonable requirements.</p><p>Ohio Residents: Ohio laws against discrimination require that all creditors make credit equally available to all creditworthy customers and that credit reporting agencies maintain separate credit histories on each individual upon request. The Ohio Civil Rights Commission administers compliance with this law.</p><p>New Hampshire Residents: If you are applying for a balloon payment contract, you are entitled, it you ask, to receive a written estimate of the monthly payment amount for refinancing the balloon payment in accord with the creditor's existing refinance programs. You would be entitled to receive the estimate before you enter into a balloon payment contract. A balloon contract is an installment sale contract with a final scheduled payment that is at least twice the amount of one of the earlier scheduled equal periodic installment payments.</p><p>New York Residents: In connection with your application for credit, we may request a consumer report that contains information on your credit worthiness, credit standing, personal characteristics and general reputation. If we grant you credit, we or our servicer may order additional consumer reports in connection with any update, renewal or extension of the credit. If you ask us, we will tell you whether we obtained a consumer report and if we did, we will tell you the name and address of the consumer reporting agency that gave us the report.</p><p>Vermont Residents: By clicking on "Submit", you authorize us and our employees or agents to obtain and verify information about you (including one or more credit reports, information about your employment and banking and credit relationships) that we may deem necessary or appropriate in evaluating your application. If your application is approved and credit is extended, you also authorize us, and our employees and agents, to obtain additional credit reports and other information about you in connection with reviewing the account, increasing the available credit on the account (if applicable), taking collection on the account, or for any other legitimate purpose.</p><p>Married Wisconsin Residents: Wisconsin law provides that no provision of any marital property agreement, or unilateral statement, or court order applied to marital property will adversely affect a creditor's interests unless, prior to the time that the credit is granted, the creditor is furnished with a copy of the agreement, statement or decree, or has actual knowledge of the adverse provision. If you are making this application individually, and not jointly with your spouse, the dealer may contact you for the full name and current address of your spouse, which must be properly disclosed.</p><p>By clicking on "Submit", I acknowledge and declare that I have read and agree with the Terms of Use. I certify that I have provided complete and true information in this application.</p>
    </b-modal>
    <b-modal v-model="privacyModal" :title="$t('messages.privacyPolicy')" @ok="privacyModal = false" size="lg">
      <p>Our dealership takes your privacy seriously and is committed to safeguarding your privacy online. Because we do gather certain types of information from visitors to our sites, we have developed a privacy statement to help you understand the terms and conditions surrounding the collection and use of that information. This statement discloses the types of information we gather, how it is used, and how you can gain access to and edit any data that we've collected about you at any time.</p><h5>Guiding Principles:</h5><p>Our dealership respects the privacy of individuals who visit our Web sites, send us e-mail, or participate in features/services we offer online. Accordingly, our policy regarding online privacy has been developed using the following guiding principles:</p><p>Our dealership maintains a strict "no-spam" policy. Subscribers to our e-mail services (or any other feature/service found on our Web site) will not receive unsolicited e-mail messages from us. Our dealership collects information online primarily to provide our visitors with a more relevant experience on our sites. When doing so, we will make every reasonable effort to avoid excessive or irrelevant collection of data. Our dealership will take reasonable physical, electronic and managerial measures to safeguard and secure any information you provide to us (e.g. data will be stored in protected databases on secured servers with restricted access). Our dealership will not share any information you've provided to us with anyone without your consent other than to provide the service you've requested. At the time you register for any such service, you will be notified of, and asked to consent to, the sharing of your information with any particular third party necessary to the provision of the requested service. </p><h5>Non-Personally Identifiable Data:</h5><p>Anytime you visit our Web site, we may gather certain non-personally identifiable information regarding the means you use to access our site. This information may include the type and version of your browser, your service provider, your IP address and any search engine you may have used to locate the site. We use this information to help diagnose problems with our server, administer the Web site, and compile broad statistical data. In addition, we gather certain navigational information about where you go on our Web sites. This information enables us to determine which areas of the Web sites are most frequently visited and helps us to tailor the sites to the needs and interests of our online visitors. Such information is gathered by us in the aggregate and will not be associated with a specific individual without that individual's consent.</p><h5>Information Voluntarily Provided:</h5><p>Any information you provide to us (i.e. name, e-mail address, etc.) when you enroll in one of our E-Mail Reminder Services, request information, or use any of the other interactive portions of our Web sites, is securely maintained on our Web server and internal systems. This information may be used to provide you with information you've requested about our company, our products and our services, or to provide you with special notices. You may opt out of receiving future communications at any time (see opt-out Procedures below). This data may also be used to tailor your experience on our sites by providing content that is relevant to your interests and geographic region.</p><h5>Information Collected from Other Sources:</h5><p>To help us better understand and respond to your needs and interests, we may in the future receive information about you from other sources. We will ask any provider of such information to represent and warrant that the information has been gathered and maintained in accordance with all state and federal laws. Any such information will be maintained by us in accordance with the standards set forth in this privacy policy along with other personal information you've provided.</p><h5>Disclosure of Personal Information:</h5><p>Our dealership understands the importance of your privacy. We collect and maintain the personal information of our consumers for our use and our subsidiaries. We do not sell, rent, or trade consumer information to third parties, other than as described below, and never without your consent. Third parties may collect personally identifiable information about your online activities over time and across different websites when you use this website.</p><h5>Business Partners:</h5><p>Our dealership may partner with select retailers and other companies at various times to provide expanded services to our site visitors. As part of such a relationship, we may share with these companies the use of certain interactive Web site functions (i.e. the E-Mail Reminder Service). Information provided by site visitors through the use of such interactive functions may be available to both our dealership and our partners.</p><h5>Agents:</h5><p>Our dealership may use other companies and individuals to perform certain functions on our behalf. Examples include administering e-mail services and running special promotions. Such parties only have access to the personal information needed to perform these functions and may not use or store the information for any other purpose. Subscribers or site visitors will never receive unsolicited e-mail messages from agents working on our behalf.</p><h5>Business Transfers:</h5><p>In the event that our dealership sells or transfers a particular portion of its business assets, consumer information may be one of the business assets transferred as part of the transaction. If substantially all of the assets of our dealership are acquired, consumer information may be transferred as part of the acquisition.</p><h5>Compliance with Law/Safety:</h5><p>Our dealership may disclose specific personal information based on a good faith belief that such disclosure is necessary to comply with or conform to the law or that such disclosure is necessary to protect the users of our Web sites, the sites themselves, or the public.</p><h5>With Consent:</h5><p>Other than as set forth in this Privacy Policy, you will be notified when any personally-identifiable information about you may be shared with third parties, and you will be given the opportunity to decline to share that information.</p><h5>Use of Cookies:</h5><p>Our dealership may use a browser feature known as a "cookie." Cookies are small files placed on your hard drive that assist us in providing you with a customized browsing experience. Our dealership uses cookies to provide you with the convenience of not having to reenter information, such as user IDs and passwords, multiple times during your visits to our Web sites. Cookies can also be utilized to help us provide you with information targeted to your interests, based upon your prior browsing on our Web sites. The "help" section of the toolbar on most browsers will inform you on how to prevent your browser from accepting new cookies, how to have the browser notify you upon the receipt of a new cookie, or how to disable the use of cookies completely. As our Web sites have been designed to take advantage of the use of cookies. Should you configure your browser to decline their use, certain features of our Web sites may not function correctly and you may be required to renter any user IDs and passwords more frequently</p><h5>Accessing Our Web Sites from Outside of the United States:</h5><p>If you are visiting our Web site from a location outside of the U.S., your connection will be through and to servers located in the U.S. Any information you provide during your visit will be processed and maintained on our Web server and other internal systems located within the U.S.</p><h5>External Links:</h5><p>Our Web sites contain links to other sites. Our dealership is not responsible for the privacy practices or the content of such Web sites. To help ensure the protection of your privacy, we recommend that you review the privacy policy of any site you visit via a link from one of our Web sites.</p><h5>Passwords:</h5><p>The personal data record created through your registration with our Web site can only be accessed with the unique password associated with that record. To protect the integrity of the information contained in this record, you should not disclose or otherwise reveal your password to third parties.</p><h5>Public Forums:</h5><p>Our Web sites may make chat rooms, forums, message boards, and/or news groups available to its users at various times. Please remember that, unless otherwise stated, any information that is disclosed in these areas becomes public information and you should exercise caution when deciding to disclose your personal information in these areas.</p><h5>Opt-out Procedures:</h5><p>You may always opt-out of receiving future communications from our dealership. Our sites allow users to opt-out of receiving such communications at the point where personal information is requested, or through other means as identified.</p><h5>Browser-Based Do Not Track:</h5><p>Some browsers incorporate a "Do Not Track" (DNT) feature that, when turned on, signals to websites and online services that you do not want to be tracked. &nbsp;Because there is not yet an accepted standard for how to respond to a DNT signal, we do not currently respond to DNT signals on this website or on websites where we provide advertisements, content, or other services.</p><h5>Contacting the Web Sites:</h5><p>If you have questions concerning this Privacy Policy or the practices of these sites, you may contact us.</p>
    </b-modal>
  </div>
</template>

<script>
import MaskedInput from 'vue-text-mask'
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe'
import Multiselect from 'vue-multiselect'
import { mapActions, mapGetters } from 'vuex';
import vSelect from 'vue-select'
import { setupCalendar, DatePicker } from 'v-calendar'
import 'v-calendar/lib/v-calendar.min.css'
import stateList from './us-states';
import canadaProvincList from './ca-provinces';
import yearsOptions from './years-options';
import monthsOptions from './months-options';
import VueNotifications from 'vue-notifications';

setupCalendar({
  firstDayOfWeek: 2 // Monday
})

const otherIncomeOptions = [{
    text: 'Select',
    value: null
}, {
    text: 'Salary/Wages',
    value: 'Salary/Wages'
}, {
    text: 'Incentive or Bonus Income',
    value: 'Incentive or Bonus Income'
}, {
    text: 'Retirement',
    value: 'Retirement'
}, {
    text: 'Child Support**',
    value: 'Child Support**'
}, {
    text: 'Family or Spousal Support (Alimony)**',
    value: 'Family or Spousal Support (Alimony)**'
}, {
    text: 'Disability',
    value: 'Disability'
}, {
    text: 'Housing Allowance',
    value: 'Housing Allowance'
}, {
    text: 'Military Allowance',
    value: 'Military Allowance'
}, {
    text: 'Municipal Bond Interest',
    value: 'Municipal Bond Interest'
}, {
    text: 'Public Assistance Programs',
    value: 'Public Assistance Programs'
}, {
    text: 'Social Security Benefits',
    value: 'Social Security Benefits'
}, {
    text: 'Workers\' Compensation',
    value: 'Workers\' Compensation'
}, {
    text: 'Other (taxable)',
    value: 'Other (taxable)'
}, {
    text: 'Other (non-taxable)',
    value: 'Other (non-taxable)'
}];

const salaryTypeOptions = [{
    text: 'Select',
    value: null
}, {
    text: 'Weekly',
    value: 'Weekly'
}, {
    text: 'Bi-Weekly',
    value: 'Bi-Weekly'
}, {
    text: 'Monthly',
    value: 'Monthly'
}, {
    text: 'Annualy',
    value: 'Annualy'
}];

const stateOptions = stateList.concat(canadaProvincList);

const today = new Date()

export default {
  name: 'advanced-forms',
  components: {
    MaskedInput,
    Multiselect,
    vSelect,
    'v-date-picker': DatePicker
  },
  beforeMount() {
      const application = this.$store.getters['creditapps/currentCreditApp'];
      console.log(application)
      if (application && application.id)  {
        this.applicant = Object.assign({}, application);
        console.log(this.applicant.employment_0_state)
        this.applicant.ssn = '';
      }
  },
  data() {
    return {
      applicant: {employment_status: null},
      termsModal: false,
      privacyModal: false,
      submitting: false,
      ssnTouched: false,
      dobTouched: false,
      phoneTouches: {},
      slug: '',
      otherIncomeOptions,
      yearsOptions,
      monthsOptions,
      stateOptions,
      salaryTypeOptions,
      states: stateOptions,
      totalYearsNeeded: 2,
      employmentsNeeded: 1,
      totalEmploymentTime: 0,
      taxid: '',
      eye: '',
      ccn: '',
      multi: null,
      selected: null,
      selectedDate: {
        start: new Date(today.getFullYear(), today.getMonth(), 7),
        end: new Date(today.getFullYear(), today.getMonth(), 11)
      }
    }
  },
  notifications: {
    showSuccessMsg: {
      type: VueNotifications.types.success,
      title: 'Success',
      message: 'Successfully saved.'
    },
    showErrorMsg: {
      type: VueNotifications.types.error,
      title: 'Oops',
      message: 'There was an error.'
    }
  },
  computed: {
    ...mapGetters({
        currentCreditApp: 'creditapps/currentCreditApp',
    }),
    employmentOptions() {
      return [
        { text: this.$t('messages.select'), value: null, disabled: true },
        { text: this.$t('messages.employed'), value: 'Employed', disabled: false },
        { text: this.$t('messages.unemployed'), value: 'Unemployed', disabled: false },
        { text: this.$t('messages.retired'), value: 'Retired', disabled: false },
        { text: this.$t('messages.activeMilitary'), value: 'Active Military', disabled: false },
        { text: this.$t('messages.retiredMilitary'), value: 'Retired Military', disabled: false },
        { text: this.$t('messages.other'), value: 'Other', disabled: false },
        { text: this.$t('messages.selfEmployed'), value: 'Self-Employed', disabled: false },
        { text: this.$t('messages.student'), value: 'Student', disabled: false },
      ]
    },
    showEmploymentFields() {
      let showFields = ['Employed', 'Active Military', 'Other', 'Self-Employed', 'Student'].includes(this.applicant.employment_status);
      console.log(showFields);
      return showFields;
    },
    rangeStart: function () { return new Date(this.selectedDate.start).toLocaleDateString() },
    rangeEnd: function () { return new Date(this.selectedDate.end).toLocaleDateString() },
  },
  mounted() {
    window.$validator = this.$validator;
  },
  methods: {
    ...mapActions({
        patchCreditApp: 'creditapps/patchCreditApp',
    }),
    phoneValidationCssClass(index) {
      if (!this.phoneTouches[`employment${index}PhoneTouched`]) {
        return '';
      }
      const re = /^\(\d{3}\) \d{3}-\d{4}$/
      return re.test(this.applicant[`employment_${index}_contact_phone`]) ? 'is-valid' : 'is-invalid';
    },
    isNumber(evt, ref) {
      evt = (evt) ? evt : window.event;
      var charCode = (evt.which) ? evt.which : evt.keyCode;
      if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
        evt.preventDefault();
      } else {
        if(ref === 'applicant.ssn') {
          this.ssnTouched = true;
        }
        if(ref === 'applicant.dob') {
          this.dobTouched = true;
        }
        return true;
      }
    },
    submit() {
            const applicantKeys = Object.keys(this.applicant);
            this.submitting = true;
            applicantKeys.forEach((key) => {
                if (key.startsWith('employment_') && this.applicant[key] === '') {
                    delete this.applicant[key];
                }
            })
            this.patchCreditApp(this.applicant).then((res) => {
                this.submitting = false;
                this.$router.push({ name: 'ShortThanks', params: {clientId: this.$route.params.clientId, leadId: this.$route.params.leadId} });
            }).catch((err) => {
                this.submitting = false;
                this.showErrorMsg();
            });
    },
    autoCorrectedDatePipe: () => { return createAutoCorrectedDatePipe('mm/dd/yyyy') },
    validateState(ref) {
      // mask field act funny
      if (ref === 'applicant.ssn' && !this.ssnTouched) {
          return null;
      }
      if (ref === 'applicant.dob' && !this.dobTouched) {
          return null;
      }
      if (this.veeFields[ref] && (this.veeFields[ref].dirty || this.veeFields[ref].validated)) {
        return !this.veeErrors.has(ref)
      }
      return null
    }
  }
}
</script>
<style lang="scss">
@media only screen and (max-width: 767px) {
  .card-footer .btn {
    display: block;
    width: 100%;
  }
}
</style>
